export type JobStatus = 'idle' | 'uploading' | 'queued' | 'processing' | 'done' | 'error';

export type UpscaleResult = {
  originalUrl: string;
  upscaledUrl: string;
  scale: number;
  width: number;
  height: number;
  originalWidth: number;
  originalHeight: number;
  outputSize: number;
  outputFormat: string;
  processingMs: number;
};

export type UploadResponse = {
  uploadId: string;
  status: JobStatus;
};

export type StatusResponse = {
  status: Exclude<JobStatus, 'idle' | 'uploading'> | 'pending' | 'completed' | 'failed';
  resultUrl?: string;
  error?: string | { code?: ApiErrorCode; message?: string };
  errorCode?: ApiErrorCode;
};

export type UsageResponse = {
  dailyFreeUsed: number;
  dailyFreeLimit: number;
};

type BackendUsageResponse = UsageResponse & {
  dailyUsed?: number;
  dailyLimit?: number;
};

export type ApiErrorCode =
  | 'daily_quota_exceeded'
  | 'rate_limited'
  | 'processing_failed'
  | 'file_too_large'
  | 'invalid_format'
  | 'unauthorized'
  | 'not_found'
  | 'service_overloaded'
  | 'unknown_error';

export class ApiError extends Error {
  code: ApiErrorCode;
  status: number;
  details?: Record<string, unknown>;

  constructor(
    message: string,
    code: ApiErrorCode,
    status: number,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE || '';

function getDeviceId(): string {
  if (typeof window === 'undefined') return '';
  const STORAGE_KEY = 'ie_device_id_v1';
  let id = localStorage.getItem(STORAGE_KEY);
  if (!id) {
    id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(STORAGE_KEY, id);
  }
  return id;
}

async function parseError(response: Response): Promise<ApiError> {
  let code: ApiErrorCode = 'unknown_error';
  let message = 'Something went wrong. Please try again.';
  let details: Record<string, unknown> | undefined;

  try {
    const data = await response.json();
    code = data.code || code;
    message = data.message || data.error || message;
    details = data.details;
  } catch {
    const text = await response.text().catch(() => '');
    if (text) message = text.slice(0, 200);
  }

  return new ApiError(message, code, response.status, details);
}

async function fetchWithDevice(
  input: string,
  init: RequestInit = {}
): Promise<Response> {
  const url = `${API_BASE}${input}`;
  const headers = new Headers(init.headers);
  const deviceId = getDeviceId();
  if (deviceId) headers.set('X-Device-Id', deviceId);
  return fetch(url, { ...init, headers });
}

export async function uploadImage(file: File, scale: number): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('scale', String(scale));

  const response = await fetchWithDevice('/api/upload', {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw await parseError(response);
  }

  const data = (await response.json()) as UploadResponse;
  return data;
}

export async function pollStatus(
  uploadId: string,
  onProgress?: (status: JobStatus, progress: number) => void
): Promise<StatusResponse> {
  let delay = 1000;
  const maxDelay = 8000;
  const timeoutMs = 10 * 60 * 1000; // 10 minutes max
  const startedAt = Date.now();

  const report = (status: JobStatus, progress: number) => {
    onProgress?.(status, progress);
  };

  report('queued', 30);

  while (Date.now() - startedAt < timeoutMs) {
    const response = await fetchWithDevice(`/api/status/${encodeURIComponent(uploadId)}`);

    if (!response.ok) {
      throw await parseError(response);
    }

    const data = (await response.json()) as StatusResponse;

    if (data.status === 'completed') {
      return data;
    }

    if (data.status === 'failed' || data.status === 'error') {
      const errorMessage = typeof data.error === 'object'
        ? data.error.message
        : data.error;
      const errorCode = typeof data.error === 'object'
        ? data.error.code
        : data.errorCode;
      report('error', 0);
      return {
        status: 'error',
        error: errorMessage || 'Processing failed. Please try again.',
        errorCode: errorCode || 'processing_failed',
      };
    }

    if (data.status === 'processing') {
      report('processing', 70);
    } else {
      report('queued', 40);
    }

    await new Promise((resolve) => setTimeout(resolve, delay));
    delay = Math.min(delay * 2, maxDelay);
  }

  report('error', 0);
  return {
    status: 'error',
    error: 'Processing took too long. Please try again later.',
    errorCode: 'processing_failed',
  };
}

export async function downloadResult(uploadId: string): Promise<Blob> {
  const response = await fetchWithDevice(`/api/download/${encodeURIComponent(uploadId)}`);

  if (!response.ok) {
    throw await parseError(response);
  }

  return response.blob();
}

export async function getUsage(): Promise<UsageResponse> {
  const response = await fetchWithDevice('/api/usage');

  if (!response.ok) {
    throw await parseError(response);
  }

  const data = (await response.json()) as BackendUsageResponse;
  return {
    dailyFreeUsed: data.dailyFreeUsed ?? data.dailyUsed ?? 0,
    dailyFreeLimit: data.dailyFreeLimit ?? data.dailyLimit ?? 2,
  };
}

export function getErrorMessage(code?: ApiErrorCode): string {
  switch (code) {
    case 'daily_quota_exceeded':
      return "You've reached today's free limit. Upgrade to Pro for more.";
    case 'rate_limited':
      return 'Too many requests. Please wait a moment and try again.';
    case 'processing_failed':
      return 'AI processing failed. Please try a different image or retry.';
    case 'file_too_large':
      return 'File size must be 5MB or smaller.';
    case 'invalid_format':
      return 'Please upload a JPG, PNG, or WebP image.';
    case 'unauthorized':
      return 'Session expired. Please refresh the page and try again.';
    case 'not_found':
      return 'Result not found. Please re-upload your image.';
    case 'service_overloaded':
      return 'Service is busy. Please try again in a few minutes.';
    default:
      return 'Something went wrong. Please try again.';
  }
}
